/**
 * This script manages dynamic properties on cart line items for preorder/backorder products.
 * 
 * It ensures that properties (like delivery dates) are correctly added or removed from cart items
 * based on inventory quantities and whether items are eligible for backorder/preorder. The script:
 * 
 * - Listens to cart:refreshed events to detect quantity changes
 * - Determines when to add/remove dynamic properties based on:
 *   - Current cart quantity vs inventory quantity
 *   - Whether cart items already have backorder/preorder properties
 *   - Whether the item is eligible for backorder
 * - Locks the cart drawer during property updates to prevent user interaction
 * - Runs on initial page load and when returning from checkout (BFCache restoration)
 * 
 * Dynamic properties are only meant to be present in checkout, providing a simple solution
 * for managing preorder/backorder product metadata.
 */

// Debug flag - controlled by theme setting
const DEBUG = window.themeVariables?.settings?.debugCartProperties ?? false;

async function lockCartDrawer() {
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  const cartDrawer = document.querySelector('cart-drawer#cart-drawer');

  if (cartDrawer) {
    cartDrawer.classList.add('is-locked'); // lock cart drawer
  } 
  
  const checkoutBtn = cartDrawer.querySelector(
    'div[slot="footer"] form#cart_form .cart-drawer__footer-inner .button-group button[name="checkout"]'
  );

  if (checkoutBtn) {
    checkoutBtn.disabled = true; // lock cart drawer
  } 
}

async function unlockCartDrawer() {
  document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
  const cartDrawer = document.querySelector('cart-drawer#cart-drawer');
  
  if (cartDrawer) {
    cartDrawer.classList.remove('is-locked'); // unlock cart drawer
  } 

  const checkoutBtn = cartDrawer.querySelector(
    'div[slot="footer"] form#cart_form .cart-drawer__footer-inner .button-group button[name="checkout"]'
  );

  if (checkoutBtn) {
    checkoutBtn.disabled = false; // unlock cart drawer
  } else {
  }
}

async function addDynamicProperties(pendingVariantIds) {
  let selector = '';
  
  if (pendingVariantIds && pendingVariantIds.length > 0) {
    pendingVariantIds.forEach((id, index) => {
      if (index === 0) {
        selector += `line-item[data-dynamic-properties][data-variant-id="${id}"]`;
      } else {
        selector += `, line-item[data-dynamic-properties][data-variant-id="${id}"]`;
      }
    });
  } else {
    // Fallback to all items if no specific IDs provided
    selector = `line-item[data-dynamic-properties]`;
  }

  const dynamicLineItems = document.querySelectorAll(selector);

  if (dynamicLineItems.length === 0) {    
    unlockCartDrawer();
    return;
  }
  lockCartDrawer();
  try {
    // Fetch current cart to get actual quantities
    const cart = await fetch(`${Shopify.routes.root}cart.js`).then(r => r.json());
    // Create a map of line keys to cart item data
    const cartQuantityMap = new Map();
    const cartItemDataMap = new Map();
    cart.items.forEach(item => {
      cartQuantityMap.set(item.key, item.quantity);
      cartItemDataMap.set(item.key, {
        productTitle: item.product_title,
        variantTitle: item.variant_title,
        properties: item.properties || {}
      });
    });

    let updatedCount = 0;
    const updatedItems = [];
    await (async () => {
      for (const item of dynamicLineItems) {
        const properties = JSON.parse(item.getAttribute('data-dynamic-properties'));
        if (!properties) continue;

        const lineKey = item.getAttribute('data-line-key');
        if (!lineKey) continue;

        const quantity = cartQuantityMap.get(lineKey);
        if (!quantity) continue;
        
        await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity, properties })
        });
        updatedCount++;
        const itemData = cartItemDataMap.get(lineKey);
        if (itemData) {
          const propertiesStr = Object.entries(properties).map(([key, value]) => `${key}: ${value}`).join(', ');
          updatedItems.push(`${itemData.productTitle}${itemData.variantTitle !== 'Default Title' ? ` (${itemData.variantTitle})` : ''}${propertiesStr ? ` [${propertiesStr}]` : ''}`);
        }
      }
    })();
    
    if (updatedCount > 0 && DEBUG) {
      console.log(`[Cart Properties] Updated ${updatedCount} item(s):\n${updatedItems.join('\n')}`);
    }
  } catch (error) {
    if (DEBUG) {
      console.error('[Cart Properties] Update failed:', error);
    }
    alert('Error updating cart. Please try again.');
    unlockCartDrawer();
  } finally {
     // Dispatch the refresh event
    document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail: { source: 'addDynamicProperties' } }));
    unlockCartDrawer();
  }
};

document.addEventListener('cart:refreshed', async (event) => {
  // Skip if this event was triggered by our own property update to avoid infinite loops
  if (event.detail?.source === 'addDynamicProperties' || !event.detail?.hasItemEligibleForBackorder || !event.detail?.variantId) {
    return;
  }

  const variantId = event.detail?.variantId;
  const newQuantity = Number(event.detail?.newQuantity);
  const currentQuantity = Number(event.detail?.currentQuantity);
  const cartQuantity = Number(event.detail?.cartQuantity);
  const inventoryQuantity = Number(event.detail?.inventoryQuantity);
  const cartItemHasBackorderOrPreorderProperty = event.detail?.cartItemHasBackorderOrPreorderProperty;
  const thisItemHasBackorderOrPreorderProperty = event.detail?.thisItemHasBackorderOrPreorderProperty;
  const changeInQuantity = newQuantity - currentQuantity;
  const newCartQuantity = cartQuantity + changeInQuantity;

  let pendingVariantIds = new Set();
  let reason = '';

  if (thisItemHasBackorderOrPreorderProperty == true && cartItemHasBackorderOrPreorderProperty == true && newCartQuantity <= inventoryQuantity) {
    pendingVariantIds.add(variantId);
    reason = 'remove (qty within inventory)';
  } else if (thisItemHasBackorderOrPreorderProperty == false && cartItemHasBackorderOrPreorderProperty == false && newCartQuantity > inventoryQuantity) {
    pendingVariantIds.add(variantId);
    reason = 'add (qty exceeds inventory)';
  } else if (thisItemHasBackorderOrPreorderProperty !== cartItemHasBackorderOrPreorderProperty) {
    pendingVariantIds.add(variantId);
    reason = 'sync (property mismatch)';
  }

  if (pendingVariantIds.size > 0) {
    if (DEBUG) {
      console.log(`[Cart Properties] Update needed: variant ${variantId}, reason: ${reason}, cartQty: ${newCartQuantity}, inventory: ${inventoryQuantity}`);
    }
    lockCartDrawer();
    setTimeout(() => addDynamicProperties(pendingVariantIds), 200);
  }
});

// Run once on initial DOM load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => addDynamicProperties(), { once: true });
} else {  
  lockCartDrawer();
  addDynamicProperties();
}

// Add Dynamic Properties When Returning from checkout
// When the page is refreshed, the script adds the dynamic properties to the cart line items.
// This is necessary when the page is restored from the browser cache and the stock has changed etc.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    // Page restored from BFCache; add dynamic properties to the cart line items.
    lockCartDrawer();
    addDynamicProperties();
  }
});